import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BloodTypeEnum } from '@models/enums/blood-type-enum';
import { DominantHandEnum } from '@models/enums/dominant-hand-enum';
import { GenreEnum } from '@models/enums/genre-enum';
import { OwnerTypeEnum } from '@models/enums/owner-type-enum';
import { EventFilter } from '@models/events/event-filter';
import { IResponseDto } from '@models/exceptions/response-dto';
import { IPage } from '@models/pagination/page';
import { PageRequest } from '@models/pagination/page-request';
import { IAttachmentDto } from '@models/portal/users/dtos/attachment-dto';
import { IContactDto } from '@models/portal/users/dtos/contact-dto';
import { IDocumentDto } from '@models/portal/users/dtos/document-dto';
import { map, Observable } from 'rxjs';
import { toParams, toUrl } from '../../utilities/utility-functions';
import { IUserEventDtoReflection } from '@models/events/user-event/dto/user-event-dto-reflection';
import { extractResponse } from '@shared/rxjs/custom-rxjs-operators';

@Injectable({
  providedIn: 'root',
})
export class UserEventService {
  private url: string = environment.apiUrl + '/usuarios';

  constructor(private httpClient: HttpClient) {}

  replicateDataToEventUser$(
    usuNrId: number,
    eveNrId: number,
  ): Observable<UserEventDto> {
    return this.httpClient
      .get<
        IResponseDto<UserEventDto>
      >(`${this.url}/${usuNrId}/eventos/${eveNrId}`)
      .pipe(map((res) => res.response));
  }

  searchUserContactsEvent$(
    usuNrId: number,
    eveNrId: number,
  ): Observable<IPage<IContactDto>> {
    return this.httpClient
      .get<
        IResponseDto<IPage<IContactDto>>
      >(`${this.url}/${usuNrId}/eventos/${eveNrId}/contatos`)
      .pipe(map((res) => res.response));
  }

  searchPublicPlaceUserEvent$(
    usuNrId: number,
    eveNrId: number,
  ): Observable<IPublicPlaceDto> {
    return this.httpClient
      .get<
        IResponseDto<IPublicPlaceDto>
      >(`${this.url}/${usuNrId}/eventos/${eveNrId}/logradouros`)
      .pipe(map((res) => res.response));
  }

  searchDocumentUserEvent$(
    usuNrId: number,
    eveNrId: number,
  ): Observable<IPage<IDocumentDto>> {
    return this.httpClient
      .get<
        IResponseDto<IPage<IDocumentDto>>
      >(`${this.url}/${usuNrId}/eventos/${eveNrId}/documentos`)
      .pipe(map((res) => res.response));
  }

  searchAttachmentsUserEvent$(
    usuNrId: number,
    eveNrId: number,
  ): Observable<IPage<IAttachmentDto>> {
    return this.httpClient
      .get<
        IResponseDto<IPage<IAttachmentDto>>
      >(`${this.url}/${usuNrId}/eventos/${eveNrId}/anexos`)
      .pipe(map((res) => res.response));
  }

  listEventsByUser$(
    usuNrId: number,
    filters?: EventFilter,
    pageable?: PageRequest,
  ): Observable<IPage<IUserEventDtoReflection>> {
    const params = toParams(filters, pageable);

    return this.httpClient
      .get<
        IResponseDto<IPage<IUserEventDtoReflection>>
      >(toUrl(this.url, usuNrId, 'eventos'), { params })
      .pipe(extractResponse());
  }
}

export interface UserEventDto {
  uevNrId: number;
  usuTxNome: string;
  usuTxSexo: GenreEnum;
  usuDtNascimento: Date;
  usuNrAltura: number;
  usuNrPeso: number;
  usuTxCodigoIep: string;
  usuTxTipoSanguineo: BloodTypeEnum;
  usuTxLadoDominante: DominantHandEnum;
  usuNrId: number;
  eveNrId: number;
  fedNrId: number;
  insNrId: number;
}

export interface IPublicPlaceDto {
  logNrId?: number;
  logTxBairro?: string;
  logTxCep?: string;
  logNrNumero?: number;
  logTxLogradouro?: string;
  logTxComplemento?: string;
  cidNrId?: number;
  estNrId?: number;
  paiNrId?: number;
  logNrIdProprietario?: number;
  logTxTipoProprietario?: OwnerTypeEnum;
}
